import React, { useState, useCallback, useRef, useEffect, DragEvent, ChangeEvent } from 'react';
import { ChevronRight, Download, Eye, EyeOff, FileText, Lock, Play, RefreshCw, Search, Upload, X } from 'lucide-react';
import { Button, Tag, Dropdown, DropdownTrigger, DropdownContent, DropdownItem, InputField } from 'equal-ds-ui';
import type { PageComponentProps } from '../../../registry/react-component-registry.constants';
import type { SandboxRunAPI } from '../../../types/analytics-sandbox';
import { analyticsSandboxAPI } from '../../../services/analytics-sandbox-api.service';
import { pdfConsoleAPI } from '../../../services/pdf-console-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';

// ── Types ──────────────────────────────────────────────────────────────────────

interface PendingFile {
  file: File;
  name: string;
  sizeLabel: string;
  isProtected: boolean;
  password: string;
}

interface DisplayRun {
  id: string;
  datasetId: string;
  status: string;
  resultType: 'json' | 'excel' | 'json_excel' | null;
  pdfMode: 'single' | 'multi' | null;
  totalItems: number | null;
  completed: number;
  failed: number;
  createdAt: string;
  transactionRef: string | null;
}

interface DisplayItem {
  sourceRef: string;
  status: 'SUCCESS' | 'FAILED' | 'PENDING';
  jsonS3Url: string | null;
  jsonResults: { index: number; journeyId?: string; url: string }[];
  excelResults: { index: number; url: string }[];
  errorMessage?: string;
  errorCode?: string | null;
  errorResponse?: any;
  isRetryable: boolean;
}

/** Build the user-facing failure message. Retryability is decided server-side (item.isRetryable).
 *  For retryable errors we show a single generic message — the exact backend text (e.g. "http 500")
 *  doesn't help the user, they just need to know they can retry.
 *  For non-retryable errors we surface GMS's message so the user knows exactly what to fix. */
function failureMessage(item: DisplayItem): string {
  if (item.isRetryable) return 'Something went wrong on our side. Try again.';
  const gmsMessage = item.errorResponse && typeof item.errorResponse === 'object' ? item.errorResponse.message : null;
  return gmsMessage || item.errorMessage || "This file couldn't be analyzed.";
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function toDisplayRun(api: SandboxRunAPI): DisplayRun {
  return {
    id: api.id,
    datasetId: api.dataset_id,
    status: api.status,
    resultType: (api as any).result_type ?? null,
    pdfMode: (api as any).pdf_mode ?? null,
    totalItems: api.total_items ?? null,
    completed: api.completed ?? 0,
    failed: api.failed ?? 0,
    createdAt: fmtDate((api as any).created_at || (api as any).createdAt),
    transactionRef: api.transaction_ref ?? null,
  };
}

function toDisplayItem(api: any): DisplayItem {
  return {
    sourceRef: api.source_ref,
    status: api.status,
    jsonS3Url: api.json_s3_url ?? null,
    jsonResults: (api.json_results || []).filter((r: any) => r.url).map((r: any) => ({ index: r.index, journeyId: r.journey_id || '', url: r.url })),
    excelResults: (api.excel_results || []).filter((r: any) => r.url),
    errorMessage: api.error_message,
    errorCode: api.error_code ?? null,
    errorResponse: api.error_response ?? null,
    isRetryable: Boolean(api.is_retryable),
  };
}

async function isPdfProtected(file: File): Promise<boolean> {
  try {
    const buf  = await file.arrayBuffer();
    const text = new TextDecoder('latin1').decode(new Uint8Array(buf));
    return /\/Encrypt[\s/<[]/.test(text);
  } catch { return false; }
}

// ── Badge configs ──────────────────────────────────────────────────────────────

const STATUS_TAG_CLASS: Record<string, string> = {
  COMPLETED:  'bg-green-100 text-green-700 rounded-full',
  PROCESSING: 'bg-amber-100 text-amber-700 rounded-full',
  PARTIAL:    'bg-orange-100 text-orange-800 rounded-full',
  FAILED:     'bg-red-100 text-red-700 rounded-full',
};

const PDF_STATUS_TAG_CLASS: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-700 rounded-full',
  PENDING: 'bg-amber-100 text-amber-700 rounded-full',
  FAILED:  'bg-red-100 text-red-700 rounded-full',
};

const TYPE_TAG_CLASS: Record<string, string> = {
  json:  'bg-blue-100 text-blue-700 rounded-full',
  excel: 'bg-emerald-100 text-emerald-700 rounded-full',
  json_excel: 'bg-violet-100 text-violet-700 rounded-full',
};


const pwdInputStyle: React.CSSProperties = {
  flex: 1, padding: '5px 9px', fontSize: 12,
  border: '1.5px solid #d1d5db', borderRadius: 5,
  backgroundColor: '#fff', color: '#374151', outline: 'none', minWidth: 0,
};

// ── Main Component ─────────────────────────────────────────────────────────────

export const PdfConsolePage: React.FC<PageComponentProps> = () => {
  const [runs, setRuns] = useState<DisplayRun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const [runResults, setRunResults] = useState<Record<string, DisplayItem[]>>({});
  const [runResultsLoading, setRunResultsLoading] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState('');
  const [txnRefDraft, setTxnRefDraft] = useState('');
  const [filterTransactionRef, setFilterTransactionRef] = useState('');
  const [newRunTxnRef, setNewRunTxnRef] = useState('');
  const [pwdVisible, setPwdVisible] = useState<Set<string>>(new Set());

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // New Run modal
  const [showNewRunModal, setShowNewRunModal] = useState(false);
  const [optionType, setOptionType] = useState<'json' | 'excel' | 'json_excel'>('json');
  const [pdfMode, setPdfMode] = useState<'single' | 'multi'>('single');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [fileError, setFileError] = useState('');
  const [modalError, setModalError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createStatus, setCreateStatus] = useState('');

  // Confirm modal
  const [showConfirm, setShowConfirm] = useState(false);

  // Download state
  const [runDownloading, setRunDownloading] = useState<Record<string, boolean>>({});
  const [runDownloadError, setRunDownloadError] = useState<Record<string, string | null>>({});
  const [retryingItems, setRetryingItems] = useState<Set<string>>(new Set());
  const [retryError, setRetryError] = useState<Record<string, string>>({});
  const [runRetryingAll, setRunRetryingAll] = useState<Set<string>>(new Set());
  const [runRetryAllError, setRunRetryAllError] = useState<Record<string, string>>({});
  const [zipDownloadEnabled, setZipDownloadEnabled] = useState<boolean>(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const downloadCancelRefs = useRef<Map<string, { cancelled: boolean }>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Refs for polling — avoid stale closures without re-creating the interval
  const runsRef = useRef(runs);
  const expandedRunsRef = useRef(expandedRuns);
  const runResultsRef = useRef(runResults);
  const pageRef = useRef(page);
  useEffect(() => { runsRef.current = runs; }, [runs]);
  useEffect(() => { expandedRunsRef.current = expandedRuns; }, [expandedRuns]);
  useEffect(() => { runResultsRef.current = runResults; }, [runResults]);
  useEffect(() => { pageRef.current = page; }, [page]);

  const { errorMessage: runsErrorMessage, handleError: handleRunsError, clearError: clearRunsError } = useErrorHandler();

  // ── Styles injection ─────────────────────────────────────────────────────────

  useEffect(() => {
    const el = document.createElement('style');
    el.id = 'pdf-console-page-styles';
    el.innerHTML = PAGE_STYLES;
    if (!document.getElementById('pdf-console-page-styles')) document.head.appendChild(el);
    return () => { document.getElementById('pdf-console-page-styles')?.remove(); };
  }, []);

  useEffect(() => {
    const refs = downloadCancelRefs.current;
    return () => { refs.forEach(ref => { ref.cancelled = true; }); };
  }, []);

  // ── Data ─────────────────────────────────────────────────────────────────────

  const loadRuns = useCallback(async (targetPage: number, silent = false, txnRefOverride?: string): Promise<DisplayRun[] | null> => {
    if (!silent) setIsLoading(true);
    clearRunsError();
    try {
      const { runs: apiRuns, total: apiTotal } = await pdfConsoleAPI.getRuns({
        page: targetPage,
        limit: pageSize,
        status: filterStatus || undefined,
        transactionRef: txnRefOverride !== undefined ? (txnRefOverride || undefined) : (filterTransactionRef || undefined),
      });
      const mapped = apiRuns.map(toDisplayRun);
      setRuns(mapped);
      setTotal(apiTotal);
      return mapped;
    } catch (err) {
      handleRunsError(err, { component: 'PdfConsolePage', action: 'loadRuns' });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [pageSize, filterStatus, filterTransactionRef, handleRunsError, clearRunsError]);

  useEffect(() => {
    setPage(1);
    loadRuns(1);
  }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadRuns(page); }, []); // initial load // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    analyticsSandboxAPI.getJourneys()
      .then(j => {
        if (cancelled) return;
        if (j.zip_download_enabled !== undefined) setZipDownloadEnabled(j.zip_download_enabled);
      })
      .catch(() => { /* keep default true */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      const prevRuns = runsRef.current;
      const hasProcessing = prevRuns.some(r => r.status === 'PROCESSING');
      const hasExpandedPending = [...expandedRunsRef.current].some(runId =>
        (runResultsRef.current[runId] || []).some(item => item.status === 'PENDING')
      );
      if (!hasProcessing && !hasExpandedPending) return;

      // loadRuns returns the freshly-mapped runs so we can use them in the same tick;
      // runsRef.current isn't synced until React re-renders, which happens after this await.
      const updatedRuns = (await loadRuns(pageRef.current, true)) ?? prevRuns;

      // Detect runs that just transitioned PROCESSING → terminal. For those that are
      // expanded, force an immediate per-PDF refresh so the children don't lag behind
      // the parent's COMPLETED/PARTIAL/FAILED status.
      const justFinished = new Set(
        updatedRuns
          .filter(r => r.status !== 'PROCESSING')
          .filter(r => prevRuns.find(p => p.id === r.id)?.status === 'PROCESSING')
          .map(r => r.id)
      );

      for (const runId of expandedRunsRef.current) {
        const items = runResultsRef.current[runId] || [];
        const needsRefresh = justFinished.has(runId) || items.some(item => item.status === 'PENDING');
        if (!needsRefresh) continue;
        try {
          const { items: fresh } = await pdfConsoleAPI.getRunResults(runId, 1, 100);
          setRunResults(prev => ({ ...prev, [runId]: fresh.map(toDisplayItem) }));
        } catch { /* ignore */ }
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [loadRuns]);

  async function handleRefresh() {
    setRefreshing(true);
    await loadRuns(page, true);
    setRefreshing(false);
  }

  async function goToPage(p: number) {
    setPage(p);
    await loadRuns(p);
  }

  // ── Expand ───────────────────────────────────────────────────────────────────

  async function toggleExpandRun(runId: string) {
    const next = new Set(expandedRuns);
    if (next.has(runId)) { next.delete(runId); setExpandedRuns(next); return; }
    next.add(runId);
    setExpandedRuns(next);
    if (!runResults[runId]) {
      setRunResultsLoading(prev => new Set(prev).add(runId));
      try {
        const { items } = await pdfConsoleAPI.getRunResults(runId, 1, 100);
        setRunResults(prev => ({ ...prev, [runId]: items.map(toDisplayItem) }));
      } catch { /* ignore */ } finally {
        setRunResultsLoading(prev => { const s = new Set(prev); s.delete(runId); return s; });
      }
    }
  }

  // ── Files ────────────────────────────────────────────────────────────────────

  async function addFiles(files: File[]) {
    const pdfs = files.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    const results = await Promise.all(pdfs.map(async f => ({
      file: f,
      name: f.name,
      sizeLabel: (f.size / 1024 / 1024).toFixed(1) + ' MB',
      isProtected: await isPdfProtected(f),
      password: '',
    })));
    setPendingFiles(prev => {
      const existing = new Set(prev.map(p => p.name));
      return [...prev, ...results.filter(r => !existing.has(r.name))];
    });
  }

  function onFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) addFiles(Array.from(e.target.files));
    e.target.value = '';
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }

  function removeFile(name: string) {
    setPendingFiles(prev => prev.filter(f => f.name !== name));
  }

  function updatePassword(name: string, password: string) {
    setPendingFiles(prev => prev.map(f => f.name === name ? { ...f, password } : f));
  }

  // ── Modal ────────────────────────────────────────────────────────────────────

  function generateReferenceId(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    const rand = Math.floor(Math.random() * 0x10000).toString(16).toUpperCase().padStart(4, '0');
    return `REF-${y}${m}${dd}${hh}${mm}${ss}-${rand}`;
  }

  function openNewRunModal() {
    setPendingFiles([]);
    setFileError('');
    setModalError('');
    setOptionType('json');
    setIsDragging(false);
    setNewRunTxnRef(generateReferenceId());
    setPwdVisible(new Set());
    setShowNewRunModal(true);
  }

  function closeNewRunModal() {
    if (isCreating) return;
    setShowNewRunModal(false);
    setShowConfirm(false);
  }

  function handleTrigger() {
    setFileError('');
    setModalError('');
    if (!newRunTxnRef.trim()) { setModalError('Reference ID is required.'); return; }
    if (pendingFiles.length === 0) { setFileError('Please upload at least one PDF file.'); return; }
    if (pendingFiles.length > 10) { setFileError(`Too many files — max 10 per run (selected ${pendingFiles.length}).`); return; }
    const missingPasswords = pendingFiles.filter(f => f.isProtected && !f.password.trim());
    if (missingPasswords.length > 0) {
      setFileError(`Password required for: ${missingPasswords.map(f => f.name).join(', ')}`);
      return;
    }
    setShowConfirm(true);
  }

  async function confirmRun() {
    setShowConfirm(false);
    setIsCreating(true);
    setCreateStatus('Creating dataset…');
    try {
      const now = new Date();
      const datasetName = `PDF Console ${now.toISOString().slice(0, 10)} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      const dataset = await analyticsSandboxAPI.createDataset(datasetName, undefined, 'PDF_CONSOLE');

      for (let i = 0; i < pendingFiles.length; i++) {
        setCreateStatus(`Uploading ${i + 1} / ${pendingFiles.length}…`);
        const f = pendingFiles[i];
        await analyticsSandboxAPI.uploadFile(dataset.id, f.file, f.isProtected && f.password ? f.password : undefined);
      }

      setCreateStatus('Triggering run…');
      await pdfConsoleAPI.createRun(dataset.id, optionType, pendingFiles.map(f => f.name), pdfMode, newRunTxnRef || undefined);

      setShowNewRunModal(false);
      setPage(1);
      await loadRuns(1, true);
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setModalError(err?.message || 'Failed to create run');
    } finally {
      setIsCreating(false);
      setCreateStatus('');
    }
  }

  // ── Retry all retryable-failed items in a run at once.
  // Multi-mode runs have exactly one merged item; retrying it uses handleRetryItem instead,
  // so this handler is only meaningful for single-mode runs.
  async function handleRetryAllFailed(run: DisplayRun) {
    if (!run.resultType) return;
    // Multi-mode runs have one merged item — handled by individual Retry, not combined.
    // Treat NULL (legacy rows) as single-mode for safety.
    if (run.pdfMode === 'multi') return;
    const items = runResults[run.id] || [];
    const retryableFilenames = items
      .filter(item => item.status === 'FAILED' && item.isRetryable)
      .map(item => item.sourceRef);
    if (retryableFilenames.length === 0) return;
    setRunRetryingAll(prev => new Set(prev).add(run.id));
    setRunRetryAllError(prev => { const { [run.id]: _drop, ...rest } = prev; return rest; });
    try {
      const newRef = generateReferenceId();
      await pdfConsoleAPI.createRun(run.datasetId, run.resultType, retryableFilenames, 'single', newRef);
      setPage(1);
      await loadRuns(1, true);
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setRunRetryAllError(prev => ({ ...prev, [run.id]: err?.message || 'Retry failed' }));
    } finally {
      setRunRetryingAll(prev => { const s = new Set(prev); s.delete(run.id); return s; });
    }
  }

  // ── Retry a single failed item — creates a new mini-run reusing the file(s) already in S3.
  // pdfMode is preserved from the original run so a multi-mode failure retries as multi.
  async function handleRetryItem(run: DisplayRun, item: DisplayItem) {
    if (!run.resultType) return;
    const key = `${run.id}:${item.sourceRef}`;
    const pdfMode: 'single' | 'multi' = run.pdfMode === 'multi' ? 'multi' : 'single';
    // In multi mode the sourceRef is a pipe-joined list of the original filenames.
    const filenames = pdfMode === 'multi'
      ? item.sourceRef.split(' | ').filter(Boolean)
      : [item.sourceRef];
    setRetryingItems(prev => new Set(prev).add(key));
    setRetryError(prev => { const { [key]: _drop, ...rest } = prev; return rest; });
    try {
      const newRef = generateReferenceId();
      await pdfConsoleAPI.createRun(run.datasetId, run.resultType, filenames, pdfMode, newRef);
      setPage(1);
      await loadRuns(1, true);
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setRetryError(prev => ({ ...prev, [key]: err?.message || 'Retry failed' }));
    } finally {
      setRetryingItems(prev => { const s = new Set(prev); s.delete(key); return s; });
    }
  }

  // ── Download ─────────────────────────────────────────────────────────────────

  async function handleDownload(runId: string, _type: 'json' | 'excel' | 'json_excel' | null) {
    const existing = downloadCancelRefs.current.get(runId);
    if (existing) existing.cancelled = true;
    const cancellation = { cancelled: false };
    downloadCancelRefs.current.set(runId, cancellation);

    setRunDownloading(prev => ({ ...prev, [runId]: true }));
    setRunDownloadError(prev => ({ ...prev, [runId]: null }));
    try {
      const { job_id } = await pdfConsoleAPI.downloadRunResults(runId);
      if (cancellation.cancelled) return;
      let url: string | null = null;
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 3000));
        if (cancellation.cancelled) return;
        const s = await pdfConsoleAPI.getDownloadJobStatus(job_id);
        if (cancellation.cancelled) return;
        if (s.status === 'SUCCESS') { url = s.download_url || await pdfConsoleAPI.getJobDownloadUrl(job_id); break; }
        if (s.status === 'FAILED') throw new Error(s.error || 'Download failed');
      }
      if (cancellation.cancelled) return;
      if (!url) throw new Error('Download timed out');
      const a = document.createElement('a');
      a.href = url;
      a.download = `results_${runId}.zip`;
      a.click();
    } catch (err: any) {
      if (!cancellation.cancelled) {
        setRunDownloadError(prev => ({ ...prev, [runId]: err?.message || 'Download failed' }));
      }
    } finally {
      if (!cancellation.cancelled) {
        setRunDownloading(prev => ({ ...prev, [runId]: false }));
      }
      downloadCancelRefs.current.delete(runId);
    }
  }

  function cancelDownload(runId: string) {
    const ref = downloadCancelRefs.current.get(runId);
    if (ref) ref.cancelled = true;
    downloadCancelRefs.current.delete(runId);
    setRunDownloading(prev => ({ ...prev, [runId]: false }));
  }

function downloadExcel(url: string) {
    // Filename comes from the presigned URL's ResponseContentDisposition header (set server-side).
    const a = document.createElement('a');
    a.href = url;
    a.click();
  }

  // Pagination derived
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="sb-root equal-ds-root sb-page" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', padding: 0 }}>

        {/* ── Page header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 className="text-2xl font-semibold text-text-primary tracking-wide" style={{ margin: '0 0 4px' }}>PDF Console Analytics</h1>
            <p className="text-sm text-text-secondary" style={{ margin: 0 }}>Upload bank statements and get analytics back as JSON or Excel.</p>
          </div>
          <Button variant="primary" size="md" onClick={openNewRunModal} style={{ flexShrink: 0, marginTop: 4 }}>
            <Play style={{ width: 14, height: 14 }} />
            Analyze PDFs
          </Button>
        </div>

        {/* ── Filters ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Dropdown>
            <DropdownTrigger variant="outline" style={{ fontSize: 13, minWidth: 130 }}>
              {filterStatus ? filterStatus.charAt(0) + filterStatus.slice(1).toLowerCase() : 'All Statuses'}
            </DropdownTrigger>
            <DropdownContent>
              <DropdownItem onSelect={() => setFilterStatus('')}>All Statuses</DropdownItem>
              <DropdownItem onSelect={() => setFilterStatus('COMPLETED')}>Completed</DropdownItem>
              <DropdownItem onSelect={() => setFilterStatus('PROCESSING')}>Processing</DropdownItem>
              <DropdownItem onSelect={() => setFilterStatus('PARTIAL')}>Partial</DropdownItem>
              <DropdownItem onSelect={() => setFilterStatus('FAILED')}>Failed</DropdownItem>
            </DropdownContent>
          </Dropdown>
          {/* Transaction ref search — press Enter to apply, X to clear */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#9ca3af', pointerEvents: 'none', zIndex: 1 }} />
            <input
              className="pdf-search-input"
              placeholder="Reference ID"
              value={txnRefDraft}
              onChange={e => setTxnRefDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setFilterTransactionRef(txnRefDraft); setPage(1); loadRuns(1, false, txnRefDraft); } }}
            />
            {txnRefDraft && (
              <button
                onClick={() => { setTxnRefDraft(''); setFilterTransactionRef(''); setPage(1); loadRuns(1, false, ''); }}
                style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: '#9ca3af' }}
                title="Clear"
              >
                <X style={{ width: 12, height: 12 }} />
              </button>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw style={{ width: 12, height: 12, animation: refreshing ? 'sb-spin 0.7s linear infinite' : 'none' }} />
            Refresh
          </Button>
        </div>

        {/* ── Load error banner ── */}
        {runsErrorMessage && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, color: '#dc2626', marginBottom: 12 }}>
            {runsErrorMessage}
          </div>
        )}

        {/* ── Runs table ── */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e8ecf4' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 120px 100px 70px 90px 120px', borderBottom: '1px solid #f1f5f9', padding: '0 14px' }}>
            <span style={{ padding: '10px 4px' }} />
            {['Reference ID', 'Output Type', 'Status', 'PDFs', 'Created', ''].map((h, i) => (
              <span key={i} style={{ fontSize: 11.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em', padding: '10px 4px' }}>{h}</span>
            ))}
          </div>

          {isLoading && runs.length === 0 ? (
            <div style={{ padding: '48px 0', display: 'flex', justifyContent: 'center' }}>
              <svg style={{ width: 20, height: 20, animation: 'sb-spin 0.7s linear infinite', color: '#4f6ef7' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
            </div>
          ) : runs.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
              No runs found. Trigger a new run to get started.
            </div>
          ) : runs.map(run => {
            const isExpanded = expandedRuns.has(run.id);
            const isLoadingItems = runResultsLoading.has(run.id);
            const items = runResults[run.id] || [];
            const stClass = STATUS_TAG_CLASS[run.status] || 'bg-slate-100 text-slate-500 rounded-full';
            const typeClass = run.resultType ? (TYPE_TAG_CLASS[run.resultType] || '') : '';
            const pdfText = run.totalItems == null ? '—' : `${run.completed} / ${run.totalItems}`;
            const pdfColor = run.status === 'COMPLETED' ? '#15803d' : run.status === 'FAILED' ? '#dc2626' : '#9ca3af';
            const isDownloading = runDownloading[run.id];
            // Combined retry summary: count retryable vs non-retryable failed items.
            // Only meaningful for single-mode runs — multi-mode runs have exactly one merged item, handled by individual Retry.
            // NULL pdfMode (legacy rows created before the column existed) is treated as 'single'.
            let retryableCount = 0;
            let nonRetryableCount = 0;
            if (run.pdfMode !== 'multi') {
              for (const it of items) {
                if (it.status !== 'FAILED') continue;
                if (it.isRetryable) retryableCount++; else nonRetryableCount++;
              }
            }
            const totalFailedForRetry = retryableCount + nonRetryableCount;
            const isRetryingRun = runRetryingAll.has(run.id);
            const runRetryAllMsg = runRetryAllError[run.id];

            return (
              <div key={run.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                {/* Row */}
                <div
                  onClick={() => toggleExpandRun(run.id)}
                  style={{ display: 'grid', gridTemplateColumns: '28px 1fr 120px 100px 70px 90px 120px', alignItems: 'center', padding: '0 14px', cursor: 'pointer', transition: 'background .1s' }}
                  onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = '#fafbff'}
                  onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
                >
                  <div style={{ padding: '12px 0', display: 'flex', alignItems: 'center' }}>
                    <ChevronRight style={{ width: 14, height: 14, color: '#9ca3af', transition: 'transform .15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }} />
                  </div>
                  <div style={{ padding: '12px 4px', minWidth: 0 }}>
                    {run.transactionRef
                      ? <span title={run.transactionRef} style={{ fontFamily: 'monospace', fontSize: 13, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{run.transactionRef}</span>
                      : <span style={{ fontSize: 13, color: '#d1d5db' }}>—</span>}
                  </div>
                  <div style={{ padding: '12px 4px', whiteSpace: 'nowrap' }}>
                    {run.resultType
                      ? <Tag size="sm" className={typeClass}>{run.resultType === 'json_excel' ? 'Excel + JSON' : run.resultType.toUpperCase()}</Tag>
                      : <span style={{ fontSize: 12, color: '#d1d5db' }}>—</span>}
                  </div>
                  <div style={{ padding: '12px 4px' }}>
                    <Tag size="sm" className={stClass}>{run.status}</Tag>
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 500, color: pdfColor, padding: '12px 4px' }}>{pdfText}</div>
                  <div style={{ fontSize: 13, color: '#6b7280', padding: '12px 4px' }}>{run.createdAt}</div>
                  <div style={{ padding: '12px 6px 12px 4px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }} onClick={e => e.stopPropagation()}>
                    {run.status !== 'PROCESSING' && run.status !== 'FAILED' && zipDownloadEnabled && (
                      isDownloading ? (
                        <button
                          onClick={() => cancelDownload(run.id)}
                          style={{ background: '#fff', color: '#dc2626', padding: '5px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer', border: '1px solid #fca5a5', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fef2f2'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
                        >
                          <X style={{ width: 11, height: 11 }} />
                          Cancel
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDownload(run.id, run.resultType)}
                          style={{ background: '#fff', color: '#374151', padding: '5px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer', border: '1px solid #d1d5db', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
                        >
                          <Download style={{ width: 11, height: 11 }} />
                          Download ZIP
                        </button>
                      )
                    )}
                    {runDownloadError[run.id] && (
                      <span style={{ fontSize: 10, color: '#dc2626' }}>{runDownloadError[run.id]}</span>
                    )}
                  </div>
                </div>

                {/* Expanded items */}
                {isExpanded && (
                  <div style={{ background: '#f8fafc', borderTop: '1px solid #f1f5f9', padding: '12px 14px 14px 44px' }}>
                    {isLoadingItems ? (
                      <div style={{ padding: '14px 0', display: 'flex', justifyContent: 'center' }}>
                        <svg style={{ width: 20, height: 20, animation: 'sb-spin 0.7s linear infinite', color: '#4f6ef7' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
                      </div>
                    ) : run.status === 'PROCESSING' && items.length === 0 ? (
                      <div style={{ padding: '12px 0', fontSize: 13, color: '#9ca3af' }}>Run in progress — results will appear when complete.</div>
                    ) : items.length === 0 ? (
                      <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>No results available for this run.</div>
                    ) : (
                      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf4', overflow: 'hidden' }}>
                        {retryableCount > 0 && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #f1f5f9', background: '#fafbff', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                              <Button variant="secondary" size="sm" onClick={() => handleRetryAllFailed(run)} disabled={isRetryingRun}>
                                <RefreshCw style={{ width: 13, height: 13, animation: isRetryingRun ? 'sb-spin 0.7s linear infinite' : 'none' }} />
                                {isRetryingRun ? 'Retrying…' : 'Retry all failed'}
                              </Button>
                              <span style={{ fontSize: 11.5, color: '#6b7280', paddingLeft: 2 }}>
                                {nonRetryableCount > 0
                                  ? `${retryableCount} of ${totalFailedForRetry} failed file${totalFailedForRetry !== 1 ? 's' : ''} will be retried`
                                  : `${retryableCount} failed file${retryableCount !== 1 ? 's' : ''} will be retried`}
                              </span>
                              {runRetryAllMsg && (
                                <span style={{ fontSize: 11, color: '#dc2626', paddingLeft: 2 }}>{runRetryAllMsg}</span>
                              )}
                            </div>
                            {nonRetryableCount > 0 && (
                              <span title="These files failed with issues you'll need to fix yourself — wrong password, scanned image, etc." style={{ fontSize: 11.5, color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                                ⓘ {nonRetryableCount} file{nonRetryableCount !== 1 ? 's' : ''} can't be retried automatically
                              </span>
                            )}
                          </div>
                        )}
                        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 220px', borderBottom: '1px solid #f1f5f9', padding: '0 14px', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                          {['Source File', 'Status', 'Action'].map((h, i) => (
                            <span key={h} style={{ fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em', padding: '8px 4px', textAlign: i === 2 ? 'right' : 'left' }}>{h}</span>
                          ))}
                        </div>
                        {items.map((item, i) => {
                          const pbClass = PDF_STATUS_TAG_CLASS[item.status] || 'bg-slate-100 text-slate-500 rounded-full';
                          const hasDownloads = item.status === 'SUCCESS' && (item.jsonResults.length > 0 || item.excelResults.length > 0);
                          const isFailed = item.status === 'FAILED';
                          const failMsg = isFailed ? failureMessage(item) : null;
                          const retryKey = `${run.id}:${item.sourceRef}`;
                          const isRetrying = retryingItems.has(retryKey);
                          const retryErrMsg = retryError[retryKey];
                          return (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 220px', alignItems: 'flex-start', borderBottom: i < items.length - 1 ? '1px solid #f9fafb' : 'none', padding: '0 14px' }}>
                              {/* Source file */}
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '10px 4px', minWidth: 0 }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth={2} style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                                <div style={{ minWidth: 0 }}>
                                  {item.sourceRef.split(' | ').map((name, idx) => (
                                    <span key={idx} title={name} style={{ fontSize: 12.5, fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{name}</span>
                                  ))}
                                  {failMsg && <span style={{ fontSize: 11, color: '#b91c1c', display: 'block', marginTop: 2, whiteSpace: 'normal', lineHeight: 1.4 }}>{failMsg}</span>}
                                </div>
                              </div>
                              {/* Status */}
                              <div style={{ padding: '10px 4px' }}>
                                <Tag size="sm" className={pbClass}>{item.status}</Tag>
                              </div>
                              {/* Downloads / Retry action */}
                              <div style={{ padding: '6px 4px', display: 'flex', justifyContent: 'flex-end' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 96, overflowY: 'auto', alignItems: 'flex-end' }}>
                                {!hasDownloads && isFailed && (
                                  item.isRetryable ? (
                                    <>
                                      <Button variant="secondary" size="sm" onClick={() => handleRetryItem(run, item)} disabled={isRetrying}>
                                        <RefreshCw style={{ width: 12, height: 12, animation: isRetrying ? 'sb-spin 0.7s linear infinite' : 'none' }} />
                                        {isRetrying ? 'Retrying…' : 'Retry'}
                                      </Button>
                                      {retryErrMsg && <span style={{ fontSize: 10, color: '#dc2626' }}>{retryErrMsg}</span>}
                                    </>
                                  ) : (
                                    <span style={{ fontSize: 11.5, color: '#9ca3af', fontStyle: 'italic' }}>— no retry</span>
                                  )
                                )}
                                {!hasDownloads && item.status === 'PENDING' && (
                                  <span style={{ fontSize: 11.5, color: '#9ca3af', fontStyle: 'italic' }}>Pending...</span>
                                )}
                                {item.status === 'SUCCESS' && item.jsonS3Url && (
                                  <button
                                    key="json-0"
                                    title="Download JSON"
                                    onClick={() => { const a = document.createElement('a'); a.href = item.jsonS3Url!; a.click(); }}
                                    style={{ background: '#eff6ff', border: '1px solid #bfdbfe', cursor: 'pointer', padding: '5px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, color: '#1d4ed8', fontSize: 12, fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap' }}
                                    onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#dbeafe'; b.style.borderColor = '#93c5fd'; }}
                                    onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#eff6ff'; b.style.borderColor = '#bfdbfe'; }}
                                  >
                                    <Download style={{ width: 12, height: 12, flexShrink: 0 }} />
                                    <span>Download</span>
                                    <span style={{ fontSize: 9, fontWeight: 700, background: '#1d4ed8', color: '#fff', borderRadius: 3, padding: '1px 4px', letterSpacing: '.04em', flexShrink: 0 }}>JSON</span>
                                  </button>
                                )}
                                {item.status === 'SUCCESS' && !item.jsonS3Url && item.jsonResults.map(jr => (
                                  <button
                                    key={`json-${jr.index}`}
                                    title="Download JSON"
                                    onClick={() => { const a = document.createElement('a'); a.href = jr.url; a.download = item.sourceRef.replace(/\.pdf$/i, '.json'); a.click(); }}
                                    style={{ background: '#eff6ff', border: '1px solid #bfdbfe', cursor: 'pointer', padding: '5px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, color: '#1d4ed8', fontSize: 12, fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap' }}
                                    onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#dbeafe'; b.style.borderColor = '#93c5fd'; }}
                                    onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#eff6ff'; b.style.borderColor = '#bfdbfe'; }}
                                  >
                                    <Download style={{ width: 12, height: 12, flexShrink: 0 }} />
                                    <span>Download</span>
                                    <span style={{ fontSize: 9, fontWeight: 700, background: '#1d4ed8', color: '#fff', borderRadius: 3, padding: '1px 4px', letterSpacing: '.04em', flexShrink: 0 }}>JSON</span>
                                  </button>
                                ))}
                                {item.status === 'SUCCESS' && item.excelResults.map(er => (
                                  <button
                                    key={`excel-${er.index}`}
                                    title="Download Excel"
                                    onClick={() => downloadExcel(er.url)}
                                    style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', cursor: 'pointer', padding: '5px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, color: '#15803d', fontSize: 12, fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap' }}
                                    onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#dcfce7'; b.style.borderColor = '#86efac'; }}
                                    onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#f0fdf4'; b.style.borderColor = '#bbf7d0'; }}
                                  >
                                    <Download style={{ width: 12, height: 12, flexShrink: 0 }} />
                                    <span>Download</span>
                                    <span style={{ fontSize: 9, fontWeight: 700, background: '#15803d', color: '#fff', borderRadius: 3, padding: '1px 4px', letterSpacing: '.04em', flexShrink: 0 }}>EXCEL</span>
                                  </button>
                                ))}
                              </div>
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Pagination ── */}
        {total > 0 && (() => {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderTop: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>
                {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total} run{total !== 1 ? 's' : ''}
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                <Button variant="secondary" size="sm" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                  ← Prev
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '…' ? (
                      <span key={`e${i}`} style={{ padding: '4px 6px', fontSize: 13, color: '#9ca3af' }}>…</span>
                    ) : (
                      <Button key={p} onClick={() => goToPage(p as number)} variant={page === p ? 'primary' : 'secondary'} size="sm">
                        {p}
                      </Button>
                    )
                  )}
                <Button variant="secondary" size="sm" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                  Next →
                </Button>
              </div>
            </div>
          );
        })()}

      </div>

      {/* ── New Run Modal ── */}
      <Modal open={showNewRunModal} onClose={closeNewRunModal}>
        <ModalHeader title="Analyze PDFs" onClose={closeNewRunModal} />
        <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Drop zone */}
          <FormField label="Upload PDFs" required error={fileError}>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              style={{
                border: `2px dashed ${isDragging ? '#4f6ef7' : '#c7d2fe'}`,
                borderRadius: 10, padding: 24, textAlign: 'center' as const,
                background: isDragging ? '#f0f4ff' : '#f8f9ff',
                cursor: 'pointer', transition: 'border-color .15s, background .15s',
              }}
            >
              <Upload style={{ width: 34, height: 34, color: '#4f6ef7', margin: '0 auto 10px', display: 'block' }} />
              <p style={{ fontSize: 13, fontWeight: 500, color: '#374151', margin: '0 0 3px' }}>
                Drop PDF files here or <span style={{ color: '#4f6ef7' }}>browse</span>
              </p>
              <small style={{ fontSize: 11.5, color: '#9ca3af' }}>PDF files only · max 10 per run</small>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,application/pdf" style={{ display: 'none' }} onChange={onFileInputChange} />
            </div>

            {pendingFiles.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 200, overflowY: 'auto' }}>
                {pendingFiles.map(f => (
                  <div key={f.name} style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                        <FileText style={{ width: 13, height: 13, color: '#f87171', flexShrink: 0 }} />
                        <span style={{ fontSize: 12.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.name}>{f.name}</span>
                        {f.isProtected && <Lock style={{ width: 11, height: 11, color: '#f59e0b', flexShrink: 0 }} />}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{f.sizeLabel}</span>
                        <button onClick={() => removeFile(f.name)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 3, color: '#9ca3af', display: 'inline-flex' }}
                          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = '#ef4444'}
                          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'}
                        ><X style={{ width: 12, height: 12 }} /></button>
                      </div>
                    </div>
                    {f.isProtected && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Lock style={{ width: 11, height: 11, color: '#f59e0b', flexShrink: 0 }} />
                        <input
                          className="sb-pwd-field"
                          type={pwdVisible.has(f.name) ? 'text' : 'password'}
                          placeholder="Password required"
                          value={f.password}
                          onChange={e => updatePassword(f.name, e.target.value)}
                          style={pwdInputStyle}
                        />
                        <button type="button" onClick={() => setPwdVisible(prev => { const s = new Set(prev); s.has(f.name) ? s.delete(f.name) : s.add(f.name); return s; })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: '#9ca3af', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {pwdVisible.has(f.name) ? <EyeOff style={{ width: 13, height: 13 }} /> : <Eye style={{ width: 13, height: 13 }} />}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </FormField>

          {/* Results format toggle */}
          <FormField label="Results format" required>
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 4, gap: 3 }}>
              {([
                { value: 'json',       label: 'JSON',         sub: '.json per PDF' },
                { value: 'excel',      label: 'Excel',        sub: '.xlsx per PDF' },
                { value: 'json_excel', label: 'Excel + JSON', sub: '.xlsx & .json per PDF' },
              ] as const).map(opt => {
                const active = optionType === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setOptionType(opt.value)}
                    style={{
                      flex: 1, padding: '8px 10px', fontSize: 12.5,
                      border: 'none', borderRadius: 7, cursor: 'pointer',
                      background: active ? '#fff' : 'transparent',
                      color: active ? '#1e40af' : '#64748b',
                      fontWeight: active ? 600 : 400,
                      transition: 'all .15s',
                      boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)' : 'none',
                      display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 1,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: active ? 700 : 500 }}>{opt.label}</span>
                    <span style={{ fontSize: 10.5, color: active ? '#3b82f6' : '#94a3b8', fontWeight: 400 }}>{opt.sub}</span>
                  </button>
                );
              })}
            </div>
          </FormField>

          {/* Results (single vs combined) toggle */}
          <FormField label="Results">
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 4, gap: 3 }}>
              {([
                { value: 'single', label: 'Separate', sub: 'One result per file' },
                { value: 'multi',  label: 'Combined', sub: 'One result for all files' },
              ] as const).map(opt => {
                const active = pdfMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setPdfMode(opt.value)}
                    style={{
                      flex: 1, padding: '8px 10px', fontSize: 12.5,
                      border: 'none', borderRadius: 7, cursor: 'pointer',
                      background: active ? '#fff' : 'transparent',
                      color: active ? '#1e40af' : '#64748b',
                      fontWeight: active ? 600 : 400,
                      transition: 'all .15s',
                      boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)' : 'none',
                      display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 1,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: active ? 700 : 500 }}>{opt.label}</span>
                    <span style={{ fontSize: 10.5, color: active ? '#3b82f6' : '#94a3b8', fontWeight: 400 }}>{opt.sub}</span>
                  </button>
                );
              })}
            </div>
          </FormField>

          <FormField label="Reference ID" required>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <InputField
                  size="sm"
                  value={newRunTxnRef}
                  onChange={e => setNewRunTxnRef(e.target.value)}
                  placeholder="REF-YYYYMMDDHHmmss-XXXX"
                  inputClassName="font-mono"
                />
              </div>
              <Button variant="secondary" size="sm" onClick={() => setNewRunTxnRef(generateReferenceId())}>
                <RefreshCw style={{ width: 14, height: 14 }} />
                Generate
              </Button>
            </div>
            <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 5 }}>
              Auto-filled so every batch is traceable — keep it or replace with your own.
            </div>
          </FormField>

          {modalError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, color: '#dc2626' }}>
              {modalError}
            </div>
          )}

          {isCreating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1d4ed8' }}>
              <svg style={{ width: 16, height: 16, animation: 'sb-spin 0.7s linear infinite', flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
              {createStatus}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={closeNewRunModal} disabled={isCreating}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleTrigger} disabled={isCreating}>
              <Play style={{ width: 12, height: 12 }} />
              Analyze
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Confirm Modal ── */}
      <Modal open={showConfirm} onClose={() => setShowConfirm(false)} width={360}>
        <div style={{ padding: '22px 20px 18px' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 6 }}>Trigger Analytics Run?</div>
          <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginBottom: 18 }}>
            About to process <strong>{pendingFiles.length} PDF{pendingFiles.length > 1 ? 's' : ''}{pdfMode === 'multi' ? ' merged into one result' : ''}</strong> with <strong>{optionType === 'json' ? 'JSON' : optionType === 'excel' ? 'Excel' : 'Excel + JSON'}</strong> output. This cannot be undone once started.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={confirmRun}>
              <Play style={{ width: 12, height: 12 }} />
              Yes, Trigger
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

// ── Shared sub-components (same as Analytics Sandbox) ─────────────────────────

const Modal: React.FC<{ open: boolean; onClose: () => void; width?: number; children: React.ReactNode }> = ({ open, onClose, width = 480, children }) => (
  <div
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    style={{ display: open ? 'flex' : 'none', position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1202, alignItems: 'center', justifyContent: 'center' }}
  >
    <div style={{ background: '#fff', borderRadius: 13, width, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
      {children}
    </div>
  </div>
);

const ModalHeader: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
  <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{title}</span>
    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 5, fontSize: 16, color: '#6b7280', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>✕</button>
  </div>
);

const FormField: React.FC<{ label: string; required?: boolean; error?: string; children: React.ReactNode }> = ({ label, required, error, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
      {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
    {children}
    {error && <div style={{ fontSize: 11.5, color: '#ef4444', marginTop: 4 }}>{error}</div>}
  </div>
);

// ── Page styles (same pattern as Analytics Sandbox) ───────────────────────────

const PAGE_STYLES = `
  @keyframes sb-spin { to { transform: rotate(360deg); } }

  .sb-root.sb-root.sb-root .pdf-search-input {
    -webkit-appearance: none !important;
    appearance: none !important;
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;
    border-radius: 6px !important;
    padding: 0 28px 0 30px !important;
    font-size: 13px !important;
    height: 34px !important;
    line-height: 1.5 !important;
    box-shadow: none !important;
    margin: 0 !important;
    width: 200px !important;
    box-sizing: border-box !important;
    color: #374151 !important;
    outline: none !important;
    display: block !important;
  }
  .sb-root.sb-root.sb-root .pdf-search-input:focus {
    border-color: #4f6ef7 !important;
    box-shadow: 0 0 0 3px rgba(79,110,247,.10) !important;
  }
  .sb-root.sb-root.sb-root .pdf-search-input::placeholder {
    color: #94a3b8 !important;
    opacity: 1 !important;
  }

  .sb-root.sb-root.sb-root .sb-pwd-field {
    -webkit-appearance: none !important;
    appearance: none !important;
    background-color: #ffffff !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 7px !important;
    padding: 7px 11px !important;
    font-size: 13px !important;
    height: auto !important;
    line-height: 1.5 !important;
    box-shadow: none !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    color: #374151 !important;
    outline: none !important;
  }
  .sb-root.sb-root.sb-root .sb-pwd-field:focus {
    border-color: #4f6ef7 !important;
    box-shadow: 0 0 0 3px rgba(79,110,247,.10) !important;
  }
  .sb-root.sb-root.sb-root input::placeholder {
    color: #94a3b8 !important;
    opacity: 1 !important;
  }
`;
